import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Buton } from "@koclukpanel/ui";
import { turAdimGoruldu, turAtla, turDurumu } from "./api";

/**
 * KARSILAMA TURU — F-HLP-01…08
 *
 * ================================================================
 * KONTROL KULLANICIDA (F-HLP-04)
 * ================================================================
 * Atlanabiliyor, geri alinabiliyor, kapatilabiliyor. Escape tusu de
 * atliyor — bir kullaniciyi ekranda kilitlemek, ogretmenin degil
 * engellemenin yolu.
 *
 * ================================================================
 * ILERLEME HER ADIMDA KAYDEDILIYOR (F-HLP-05)
 * ================================================================
 * Turun SONUNDA kaydetseydik, yarida birakip cikan kullanici
 * sonraki giriste her seyi bastan gorurdu — ve ikinci kez yarida
 * birakirdi.
 *
 * ================================================================
 * VURGU MENUNUN KENDISINDE
 * ================================================================
 * Sartname "tek seferde tek menu vurgulanir, digerleri soluklasir"
 * diyor. Bunu yapmanin yolu menu ogesine `data-tur` koyup CSS ile
 * ayirmak; kopya bir menu cizip uzerine bindirmek, gercek menuyle
 * ayrildigi gun yanlis yeri gosterirdi.
 */
export function Tur() {
  const qc = useQueryClient();
  const [indeks, setIndeks] = useState(0);
  const [kapali, setKapali] = useState(false);

  const { data } = useQuery({ queryKey: ["tur"], queryFn: turDurumu });

  const adimIsaretle = useMutation({ mutationFn: (a: string) => turAdimGoruldu(a) });
  const atla = useMutation({
    mutationFn: () => turAtla(),
    onSuccess: () => {
      setKapali(true);
      void qc.invalidateQueries({ queryKey: ["tur"] });
    },
  });

  const adimlar = data?.adimlar ?? [];
  const aktif = adimlar[indeks];

  /**
   * Adim GORUNDUGUNDE isaretleniyor, "ileri"ye basildiginda degil.
   *
   * Ileriye baglasaydik son adim hic isaretlenmezdi ve kullanici her
   * giriste onu yeniden gorurdu.
   */
  useEffect(() => {
    if (aktif) adimIsaretle.mutate(aktif.anahtar);
    // Bagimlilik YALNIZCA anahtar: `adimIsaretle` her cizimde yeni bir
    // nesne ve listeye eklemek sonsuz donguye yol acardi.
  }, [aktif?.anahtar]);

  /** Menudeki ilgili ogeyi vurgula — digerleri soluklasir (F-HLP-01). */
  useEffect(() => {
    const govde = document.body;
    if (aktif && !kapali) {
      govde.setAttribute("data-tur-aktif", aktif.anahtar);
    } else {
      govde.removeAttribute("data-tur-aktif");
    }
    return () => govde.removeAttribute("data-tur-aktif");
  }, [aktif, kapali]);

  /** Escape ATLAR — kullaniciyi ekranda kilitlemek ogretmek degil. */
  useEffect(() => {
    const dinle = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !kapali && adimlar.length > 0) atla.mutate();
    };
    window.addEventListener("keydown", dinle);
    return () => window.removeEventListener("keydown", dinle);
    // `atla` bilerek disarida: her cizimde yenilenen bir nesne ve
    // dinleyiciyi her cizimde yeniden baglamak gereksiz.
  }, [kapali, adimlar.length]);

  if (kapali || !aktif || !data) return null;

  const sonAdim = indeks === adimlar.length - 1;
  const yeniBolum = data.tur === "YENI_BOLUM";

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="tur-baslik"
      className="bg-notr-800/70 fixed inset-0 z-50 flex items-end justify-center p-4 sm:items-center"
    >
      <div className="rounded-kart bg-notr-0 w-full max-w-md p-5 shadow-xl">
        <p className="text-marka-600 text-xs font-medium tracking-wide uppercase">
          {/*
            "Hos geldiniz" ile "yeni bolum" ayri: sistemi aylardir
            kullanan birine hos geldiniz demek sacma olurdu.
          */}
          {yeniBolum ? "Yeni bölüm" : data.tur === "DEVAM" ? "Kaldığınız yerden" : "Hoş geldiniz"}
          {adimlar.length > 1 && ` · ${indeks + 1} / ${adimlar.length}`}
        </p>

        <h2 id="tur-baslik" className="text-notr-800 mt-1 text-lg font-semibold">
          {aktif.baslik}
        </h2>
        <p className="text-notr-700 mt-2 text-sm whitespace-pre-line">{aktif.metin}</p>

        <div className="mt-5 flex flex-wrap items-center justify-between gap-2">
          <Buton gorunum="sessiz" onClick={() => atla.mutate()} disabled={atla.isPending}>
            {yeniBolum ? "Kapat" : "Turu atla"}
          </Buton>

          <div className="flex gap-2">
            {indeks > 0 && (
              <Buton gorunum="ikincil" onClick={() => setIndeks(indeks - 1)}>
                Geri
              </Buton>
            )}
            {sonAdim ? (
              <Buton onClick={() => setKapali(true)}>Bitir</Buton>
            ) : (
              <Buton onClick={() => setIndeks(indeks + 1)}>İleri</Buton>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
